import React, { useEffect, useRef } from 'react';

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const smooth = (t) => t * t * (3 - 2 * t);
// How far the light has come through [from, to], eased at both ends.
const phase = (light, from, to) => smooth(clamp01((light - from) / (to - from)));

// Where the floating chrome sits: the header near the top of the screen, the
// phone's dock near the bottom.
const HEADER_LINE = 72;
const DOCK_LINE = 72;

// Seeded, so the prerendered stars are the ones that hydrate.
const STAR_GROUPS = (() => {
  let seed = 20261013;
  const random = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  const groups = [[], [], []];
  for (let index = 0; index < 132; index += 1) {
    const size = random();
    groups[index % 3].push({
      x: `${(random() * 100).toFixed(2)}%`,
      y: `${(random() ** 1.6 * 92).toFixed(2)}%`,
      r: size > 0.95 ? 1.5 : size > 0.8 ? 1.05 : size > 0.45 ? 0.75 : 0.5,
    });
  }
  return groups;
})();

// Top to bottom. An opaque layer at full strength hides everything under it.
const LAYERS = [
  { name: 'day', opaque: true },
  { name: 'sun' },
  { name: 'sunrise', opaque: true },
  { name: 'stars' },
  { name: 'dawn', opaque: true },
  { name: 'midnight', opaque: true },
  { name: 'embers' },
  { name: 'night', opaque: true },
];

// One sky behind the whole landing page, carried by the reader's scroll from
// the embers of the Inferno to daylight. `light` runs from 0, as the mission
// rises into view below the Delacroix, to 1, once the Parnassus fresco has
// passed overhead: stars come out over the mission, dawn climbs while it is
// read, and the sun rises behind the fresco, so its brightest moments sit
// behind a painting rather than behind text.
//
// Once the day act reaches the header or the dock, html.daylight-top and
// html.daylight-bottom tell that chrome to take the day palette.
export default function CantoAtmosphere() {
  const rootRef = useRef(null);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    const root = document.documentElement;
    const dawnAct = document.querySelector('[data-act="dawn"]');
    const dayAct = document.querySelector('[data-act="day"]');
    const fresco = document.getElementById('poets-bench');
    const layers = LAYERS.map((layer) => ({
      ...layer,
      el: node.querySelector(`[data-sky="${layer.name}"]`),
      shown: null,
      opacity: null,
    }));
    const sun = node.querySelector('[data-sky="sun"]');
    let sunY = null;
    let frame = 0;

    const update = () => {
      frame = 0;
      const viewport = window.innerHeight || 1;
      const dawnTop = dawnAct ? dawnAct.getBoundingClientRect().top : viewport;
      const frescoBottom = fresco ? fresco.getBoundingClientRect().bottom : dawnTop + viewport;
      const light = clamp01(
        (viewport - dawnTop) / Math.max(1, frescoBottom - dawnTop + viewport * 0.7)
      );

      const values = {
        night: 1,
        embers: 1 - phase(light, 0, 0.16),
        midnight: phase(light, 0, 0.2),
        dawn: phase(light, 0.2, 0.48),
        stars: phase(light, 0.02, 0.22) * (1 - phase(light, 0.42, 0.64)),
        sunrise: phase(light, 0.48, 0.76),
        sun: phase(light, 0.5, 0.62),
        day: phase(light, 0.72, 0.96),
      };

      let covered = false;
      layers.forEach((layer) => {
        const value = values[layer.name];
        const shown = !covered && value > 0.001;
        if (layer.el && shown !== layer.shown) {
          layer.el.style.display = shown ? 'block' : 'none';
          layer.shown = shown;
        }
        const opacity = value.toFixed(3);
        if (layer.el && shown && opacity !== layer.opacity) {
          layer.el.style.opacity = opacity;
          layer.opacity = opacity;
        }
        if (layer.opaque && value >= 0.999) covered = true;
      });

      const nextSunY = (112 - 107 * phase(light, 0.52, 1)).toFixed(2);
      if (sun && nextSunY !== sunY) {
        sun.style.transform = `translate3d(0, ${nextSunY}vh, 0)`;
        sunY = nextSunY;
      }

      const dayTop = dayAct ? dayAct.getBoundingClientRect().top : Infinity;
      root.classList.toggle('daylight-top', dayTop < HEADER_LINE);
      root.classList.toggle('daylight-bottom', dayTop < viewport - DOCK_LINE);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // Late images and fonts move the acts without a scroll.
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(onScroll);
    if (resizeObserver) resizeObserver.observe(document.body);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (resizeObserver) resizeObserver.disconnect();
      root.classList.remove('daylight-top', 'daylight-bottom');
    };
  }, []);

  return (
    <div ref={rootRef} className="canto-atmosphere" aria-hidden="true">
      <div className="sky-layer sky-night" data-sky="night" />
      <div className="sky-layer sky-embers" data-sky="embers" />
      <div className="sky-layer sky-midnight" data-sky="midnight" />
      <div className="sky-layer sky-dawn" data-sky="dawn" />
      <div className="sky-layer sky-stars" data-sky="stars">
        {STAR_GROUPS.map((stars, group) => (
          <svg key={group} className={`sky-stars-group sky-stars-group--${group}`} focusable="false">
            {stars.map((star) => (
              <circle key={`${star.x}-${star.y}`} cx={star.x} cy={star.y} r={star.r} />
            ))}
          </svg>
        ))}
      </div>
      <div className="sky-layer sky-sunrise" data-sky="sunrise" />
      <div className="sky-sun" data-sky="sun" />
      <div className="sky-layer sky-day" data-sky="day" />
      <div className="sky-layer sky-grain" />
    </div>
  );
}
